import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListFPARequest2 } from "../../../../../../services/soap/project/requests/ListFPARequest2";
import { GetFolderRequest } from "../../../../../../services/soap/project/requests/GetFolderRequest";

interface Supplier {
  id: string;
  folderType: string;
  folderTypeName: string;
  referenceId: string;
  name: string;
  customState: string;
  customStateName: string;
  organization: string;
  created: string;
  createdByName: string;
  modified: string;
  modifiedByName: string;
  state: string;
  contextParent: string;
  contextParentName: string;
}

interface ISupplierDroDown {
  selectedSupplier?: string;
  supplierName?: string;
  onChange: (data: Supplier | null) => void;
  isEdit: any;
  fpaTypeCategory?: string;
  label?: string;
  hideLabel?: boolean;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
}

const SupplierDroDown: React.FC<ISupplierDroDown> = ({
  selectedSupplier = "",
  supplierName = "",
  onChange,
  isEdit,
  fpaTypeCategory,
  label,
  hideLabel = false,
  isRequired,
  errorMessage,
  validationState,
}) => {
  const { t } = useTranslation("invoice");
  const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
  const resolvedLabel = label ?? _t("supplier");

  const { store, projectService } = useDependency();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [resolvedSupplierName, setResolvedSupplierName] = useState<string>("");

  const initialLoadRef = useRef(true);
  const isUserEditingRef = useRef(false);
  const didInitialAutoSelectRef = useRef(false);

  const list = useAsyncList<Supplier>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 5000;
      try {
        const requestBody: any = {
          limit,
          offset,
          flatView: false,
          folderFilter: {
            COL: [
              {
                name: "fpaTypeCategories",
                type: "any",
                value: fpaTypeCategory,
              },
            ],
          },
          ORDER: {
            COL: [
              { name: "name", desc: false },
              { name: "created", desc: true },
            ],
          },
        };

        if (
          initialLoadRef.current &&
          supplierName &&
          supplierName.trim() !== "" &&
          !isEdit
        ) {
          requestBody.fulltext = supplierName + "*";
        } else if (filterText?.trim()) {
          requestBody.fulltext = filterText + "*";
        }

        const response = await projectService.listFPA2(
          new ListFPARequest2(store.Server, store.SessionId, requestBody)
        );
        const folders: Supplier[] = response?.ITEMS
          ? response.ITEMS.map((entry: any) => entry.ITEM?.FOLDER).filter(Boolean)
          : [];
        if (!Array.isArray(folders)) return { items: [] };

        if (initialLoadRef.current) {
          initialLoadRef.current = false;
        }

        return {
          items: folders,
          cursor: folders.length === limit ? String(offset + limit) : undefined,
        };
      } catch (error) {
        console.error("Error fetching Suppliers:", error);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialLoadRef.current = true;
    didInitialAutoSelectRef.current = false;
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fpaTypeCategory]);

  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedSupplier) {
      setSelectedKey(null);
      setResolvedSupplierName("");
      setInputValue("");
      return;
    }

    if (String(selectedSupplier) === "0") {
      setSelectedKey(null);
      setResolvedSupplierName("");
      setInputValue("0");
      return;
    }

    const selected = list.items.find((x) => String(x.id) === String(selectedSupplier));
    setSelectedKey(selected ? String(selectedSupplier) : null);
    setInputValue(selected?.name ?? supplierName ?? resolvedSupplierName ?? "");
  }, [selectedSupplier, supplierName, resolvedSupplierName, list.items]);

  useEffect(() => {
    let active = true;
    const loadSelectedSupplierName = async () => {
      const supplierId = String(selectedSupplier || "").trim();
      if (!supplierId || supplierId === "0") {
        if (active) setResolvedSupplierName("");
        return;
      }

      const existsInLoaded = list.items.some((x) => String(x.id) === supplierId);
      if (existsInLoaded) {
        if (active) setResolvedSupplierName("");
        return;
      }

      if (String(supplierName || "").trim()) {
        if (active) setResolvedSupplierName("");
        return;
      }

      const numericId = Number(supplierId);
      if (!Number.isFinite(numericId) || numericId <= 0) return;

      try {
        const response: any = await projectService.getFolder(
          new GetFolderRequest(store.Server, store.SessionId, numericId)
        );
        const folderName = String(
          response?.FOLDER?.name ?? response?.FOLDER?.name1 ?? ""
        ).trim();
        if (active) setResolvedSupplierName(folderName);
      } catch {
        if (active) setResolvedSupplierName("");
      }
    };

    loadSelectedSupplierName();
    return () => {
      active = false;
    };
  }, [selectedSupplier, supplierName, list.items, projectService, store.Server, store.SessionId]);

  useEffect(() => {
    if (
      didInitialAutoSelectRef.current ||
      isEdit ||
      selectedSupplier ||
      list.items.length === 0
    ) {
      return;
    }

    didInitialAutoSelectRef.current = true;
    const first = list.items[0];
    setSelectedKey(String(first.id));
    setInputValue(first.name ?? "");
    onChange(first);
  }, [list.items, isEdit, selectedSupplier, onChange]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    if (selectedKey) {
      const selected = list.items.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={hideLabel ? undefined : resolvedLabel}
      aria-label={resolvedLabel}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      onSelectionChange={handleSelectionChange}
      allowsCustomValue
      width={{ base: "100%", L: "100%" }}
      isRequired={isRequired}
      validationState={validationState}
      errorMessage={errorMessage}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default SupplierDroDown;

